import type { Dispatch } from "react";
import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import type { EditorAction } from "../editor/state";
import { pairKey } from "../editor/types";
import type { DraftPlan, Tool } from "../editor/types";

/**
 * The combinable-pair review panel (Tech-decision "Combinable pairs UX", R7/FL-2). The pair LIST — not
 * drawn adjacency — is the authority: link mode makes listing a deliberate act, and this panel is the
 * reviewable record of it. Removing a pair here is the only way to un-combine two tables; moving a
 * table on the canvas never changes this list.
 */
export interface PairListPanelProps {
  plan: DraftPlan;
  tool: Tool;
  linkAnchor: string | null;
  disabled: boolean;
  dispatch: Dispatch<EditorAction>;
}

export function PairListPanel({ plan, tool, linkAnchor, disabled, dispatch }: PairListPanelProps) {
  const { t } = useTranslation();
  const labelOf = (id: string) => plan.tables.find((tb) => tb.id === id)?.label ?? id.slice(0, 4);
  const linkMode = tool === "LINK";

  return (
    <section
      aria-label={t("floorPlan.pairs.title")}
      className={css({ display: "flex", flexDirection: "column", gap: "2" })}
    >
      <div className={css({ display: "flex", alignItems: "center", gap: "2" })}>
        <h3 className={css({ fontSize: "md", fontWeight: "semibold" })}>
          {t("floorPlan.pairs.title")}
        </h3>
        <button
          type="button"
          disabled={disabled}
          aria-pressed={linkMode}
          onClick={() => dispatch({ type: "SET_TOOL", tool: linkMode ? "SELECT" : "LINK" })}
          className={css({
            ml: "auto",
            px: "3",
            py: "1",
            rounded: "md",
            borderWidth: "1px",
            fontSize: "sm",
            bg: linkMode ? "colorPalette.default" : "transparent",
            color: linkMode ? "colorPalette.fg" : "fg.default",
          })}
        >
          {linkMode ? t("floorPlan.pairs.linkModeOn") : t("floorPlan.pairs.linkMode")}
        </button>
      </div>

      {linkMode ? (
        <p className={css({ fontSize: "xs", color: "fg.muted" })}>
          {linkAnchor
            ? t("floorPlan.pairs.pickSecond", { label: labelOf(linkAnchor) })
            : t("floorPlan.pairs.pickFirst")}
        </p>
      ) : null}

      {plan.combinables.length === 0 ? (
        <p className={css({ fontSize: "sm", color: "fg.muted" })}>{t("floorPlan.pairs.empty")}</p>
      ) : (
        <ul
          className={css({
            display: "flex",
            flexDirection: "column",
            gap: "1",
            listStyle: "none",
            p: "0",
          })}
        >
          {plan.combinables.map((p) => (
            <li
              key={pairKey(p)}
              className={css({ display: "flex", alignItems: "center", gap: "2", fontSize: "sm" })}
            >
              <span>{t("floorPlan.pairs.pair", { a: labelOf(p.a), b: labelOf(p.b) })}</span>
              <button
                type="button"
                disabled={disabled}
                aria-label={t("floorPlan.pairs.remove", { a: labelOf(p.a), b: labelOf(p.b) })}
                onClick={() => dispatch({ type: "TOGGLE_PAIR", a: p.a, b: p.b })}
                className={css({
                  ml: "auto",
                  px: "2",
                  py: "0.5",
                  rounded: "md",
                  borderWidth: "1px",
                  color: "fg.error",
                })}
              >
                ✕
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
